import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { UserAvatar } from "../components/UserAvatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Send, Camera, Loader2, ShieldCheck, Briefcase, UserRound } from "lucide-react";

const ROLE_META = {
  admin: { label: "Admin", icon: ShieldCheck, cls: "bg-primary/10 text-primary border-primary/20" },
  salesperson: { label: "Salesperson", icon: UserRound, cls: "bg-blue-50 text-blue-700 border-blue-100" },
  vendor: { label: "Vendor", icon: Briefcase, cls: "bg-amber-50 text-amber-700 border-amber-100" },
};
const STATUS_CLS = {
  active: "bg-green-50 text-green-700 border-green-200",
  invited: "bg-amber-50 text-amber-700 border-amber-100",
  contact: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "salesperson", company: "" });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (ids, on) => setSelected((s) => { const n = new Set(s); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); return n; });

  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/admin/users/bulk-delete", { ids });
      setUsers((list) => list.filter((x) => !(selected.has(x.id) && x.id !== me?.id)));
      setSelected(new Set());
      setBulkOpen(false);
      toast.success(`Removed ${data.deleted} user${data.deleted === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const load = () => {
    api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const invite = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", form);
      setUsers((u) => [data, ...u]);
      setInviteOpen(false);
      setForm({ name: "", email: "", role: "salesperson", company: "" });
      toast.success(data.role === "vendor" ? "Vendor added" : `Invite sent to ${data.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create user");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (u, role) => {
    try {
      const { data } = await api.patch(`/admin/users/${u.id}`, { role });
      setUsers((list) => list.map((x) => (x.id === u.id ? data : x)));
      toast.success("Role updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update role");
    }
  };

  const resend = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/resend-invite`);
      toast.success(`Invite re-sent to ${u.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend invite");
    }
  };

  const confirmDelete = async () => {
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await api.delete(`/admin/users/${id}`);
      setUsers((list) => list.filter((x) => x.id !== id));
      toast.success("User removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove user");
    }
  };

  const uploadAvatar = async (u, file) => {
    if (!file) return;
    setUploadingId(u.id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUsers((list) => list.map((x) => (x.id === u.id ? { ...x, avatar_url: data.avatar_url } : x)));
      toast.success("Photo updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-users">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <button onClick={() => navigate("/admin")} className="font-mono text-xs uppercase tracking-[0.2em] text-primary flex items-center gap-1 hover:underline" data-testid="back-to-overview">
            <ArrowLeft className="h-3 w-3" /> Admin Console
          </button>
          <h1 className="mt-2 font-display font-black text-4xl tracking-tighter">Users</h1>
          <p className="mt-1 font-mono text-xs text-zinc-500">Manage your team — salespeople, vendors and admins.</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} data-testid="invite-user-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">
          <Plus className="h-4 w-4 mr-1" /> Invite User
        </Button>
      </div>

      {loading ? (
        <p className="font-mono text-sm text-zinc-500">Loading…</p>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          {selected.size > 0 && (
            <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-primary/5 border-b border-primary/20" data-testid="users-bulk-bar">
              <span className="font-mono text-xs uppercase tracking-widest text-primary">{selected.size} selected</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="rounded-none font-mono text-xs uppercase tracking-widest">Clear</Button>
                <Button size="sm" data-testid="users-bulk-delete-btn" onClick={() => setBulkOpen(true)} className="rounded-none bg-red-600 hover:bg-red-700 text-white font-mono text-xs uppercase tracking-widest">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete selected
                </Button>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 font-mono text-xs uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    data-testid="users-select-all"
                    checked={users.length > 0 && users.every((u) => selected.has(u.id))}
                    onCheckedChange={(v) => toggleAll(users.map((u) => u.id), !!v)}
                  />
                </th>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const meta = ROLE_META[u.role] || ROLE_META.salesperson;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <Checkbox data-testid={`user-select-${u.id}`} checked={selected.has(u.id)} onCheckedChange={() => toggleOne(u.id)} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="relative group">
                          <UserAvatar name={u.name} email={u.email} src={u.avatar_url} className="h-10 w-10" testid={`user-avatar-${u.id}`} />
                          <button
                            onClick={() => fileRefs.current[u.id]?.click()}
                            data-testid={`upload-avatar-${u.id}`}
                            className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Upload photo"
                          >
                            {uploadingId === u.id ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Camera className="h-4 w-4 text-white" />}
                          </button>
                          <input ref={(el) => (fileRefs.current[u.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(u, e.target.files?.[0])} />
                        </div>
                        <div>
                          <p className="font-medium">{u.name || "—"}</p>
                          <p className="text-xs text-zinc-500">{u.email}</p>
                          {u.company && <p className="text-[11px] text-zinc-400">{u.company}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                        <SelectTrigger data-testid={`role-select-${u.id}`} className="w-[150px] rounded-none border-zinc-300 font-mono text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="salesperson">Salesperson</SelectItem>
                          <SelectItem value="vendor">Vendor</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${STATUS_CLS[u.status] || STATUS_CLS.contact}`} data-testid={`user-status-${u.id}`}>
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        {u.status === "invited" && (
                          <Button size="sm" variant="ghost" data-testid={`resend-invite-${u.id}`} onClick={() => resend(u)} className="rounded-none h-8 px-2 text-primary hover:bg-primary/10" title="Resend invite">
                            <Send className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" data-testid={`delete-user-${u.id}`} onClick={() => setDeleteTarget(u)} className="rounded-none h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" title="Remove user">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {users.length === 0 && <p className="p-10 text-center text-zinc-500">No team members yet. Invite your first user.</p>}
        </div>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-none border-zinc-900">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tighter">Invite a user</DialogTitle>
            <DialogDescription>
              Salespeople and admins receive an email to set their password. Vendors are saved as cost/supplier contacts (no login).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Name</Label>
              <Input data-testid="invite-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="Jane Doe" />
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-widest">Email</Label>
              <Input data-testid="invite-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="jane@brandbloom.agency" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invite-role-select" className="mt-1.5 rounded-none border-zinc-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salesperson">Salesperson</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Company (optional)</Label>
                <Input data-testid="invite-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="Acme Inc." />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="rounded-none border-zinc-300 font-mono uppercase tracking-widest text-xs">Cancel</Button>
            <Button data-testid="invite-submit-btn" disabled={saving} onClick={invite} className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest text-xs">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : form.role === "vendor" ? "Add Vendor" : "Send Invite"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {selected.size} user{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              The selected users will be removed (your own account is always skipped). Any quotes assigned to them become unassigned.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-users-bulk-delete-btn" disabled={bulkDeleting} onClick={(e) => { e.preventDefault(); bulkDelete(); }} className="rounded-none bg-red-600 hover:bg-red-700">
              {bulkDeleting ? "Removing…" : `Remove ${selected.size}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && `${deleteTarget.name || deleteTarget.email} will be removed. Any quotes assigned to them become unassigned.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user-btn" onClick={confirmDelete} className="rounded-none bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
